<login-form>
    <!--
        EULA modal is mounted globally in index.tag (id="eula-modal").
        We don't render a second instance here — duplicate IDs would
        leave document.querySelector('#eula-modal') pointing at whichever
        instance hit the DOM first (always the global one), so a local
        copy's onAccept would never fire. Instead, every login flow
        below opens the shared modal and we listen for the broadcast
        'eula-accepted' window event in onMounted to navigate post-
        acceptance.
    -->

    <!-- ================================================================ -->
    <!-- SUBDOMAIN / CUSTOMER LOGIN MODE                                -->
    <!-- Shown when the user arrives on a custom subdomain like         -->
    <!-- acme.app.metalark.ai — displays customer branding and their   -->
    <!-- configured identity providers. Hidden for bootstrap admin.     -->
    <!-- ================================================================ -->
    <div if="{state.subdomainMode}" class="card bg-base-100 shadow-xl">
        <div class="card-body items-center text-center">
            <!-- Customer branding is now hoisted to the page header in
                 login.tag (via the onBrandingResolved prop callback) so
                 it stays anchored at the top regardless of how many
                 returning-users cards push the form down. We keep a
                 small "Sign in" title here only when branding hasn't
                 resolved yet — gives the loading card a label instead
                 of a bare spinner. -->
            <div if="{!state.customerBranding}" class="mb-4">
                <h2 class="text-xl font-bold">{locale?.t('login_subdomain_signin')}</h2>
            </div>

            <!-- Loading state -->
            <div if="{state.subdomainLoading}" class="py-6">
                <span class="loading loading-spinner loading-lg"></span>
                <p class="text-sm text-gray-500 mt-2">{locale?.t('login_loadingoptions')}</p>
            </div>

            <!-- OAuth progress steps (reuse existing component) -->
            <auth-progress-steps
                if="{state.oauthInProgress}"
                currentStep="{state.progressStep}"
                statusMessage="{state.progressMessage}"
                statusType="{state.progressType}"
            ></auth-progress-steps>

            <!-- Provider buttons -->
            <div if="{!state.subdomainLoading && !state.oauthInProgress}" class="w-full space-y-3">
                <!-- Primary provider (prominent) -->
                <button if="{state.subdomainPrimaryProvider}"
                    class="btn btn-primary w-full"
                    onclick="{() => handleSubdomainProviderLogin(state.subdomainPrimaryProvider)}"
                    disabled="{state.loading}">
                    <i class="bi {getProviderIcon(state.subdomainPrimaryProvider.type)} me-2"></i>
                    {locale?.t('login_signinwith', { provider: state.subdomainPrimaryProvider.displayName })}
                </button>

                <!-- Secondary providers -->
                <div if="{state.subdomainSecondaryProviders.length > 0}" class="divider text-xs">{locale?.t('login_otheroptions')}</div>
                <button each="{provider in state.subdomainSecondaryProviders}"
                    class="btn btn-outline w-full"
                    onclick="{() => handleSubdomainProviderLogin(provider)}"
                    disabled="{state.loading}">
                    <i class="bi {getProviderIcon(provider.type)} me-2"></i>
                    {provider.displayName}
                </button>

                <!-- Misconfigured: SSO is REQUIRED for this customer but
                     no provider has been set up. Password/passkey would
                     be rejected by the backend's enforcement check, so
                     there is no recovery path from this page — surface
                     the warning instead of any sign-in affordance. -->
                <div if="{!state.subdomainPrimaryProvider && state.subdomainSecondaryProviders.length === 0 && state.subdomainSsoEnforcement === 'REQUIRED'}" class="alert alert-warning">
                    <span class="text-sm">{locale?.t('login_noproviders')}</span>
                </div>

                <!-- Password and passkey fallback (collapsed) — only shown
                     when at least one SSO provider is configured. With no
                     SSO configured AND enforcement is OPTIONAL,
                     username/password is the customer's intentional
                     sign-in method (primaryProviderId set to null in the
                     login-domain admin) and the password form opens
                     directly below as the primary CTA. -->
                <div if="{(state.subdomainPrimaryProvider || state.subdomainSecondaryProviders.length > 0) && !state.subdomainShowPassword}" class="mt-2">
                    <div class="divider text-xs">{locale?.t('login_or')}</div>
                    <button class="btn btn-ghost btn-sm w-full" onclick="{showSubdomainPasswordForm}">
                        <i class="bi bi-key me-2"></i>
                        {locale?.t('login_signinwithpassword')}
                    </button>
                    <button if="{state.passkeySupported}" class="btn btn-ghost btn-sm w-full mt-1" onclick="{handlePasskeyLogin}">
                        <i class="bi bi-fingerprint me-2"></i>
                        {locale?.t('loginform_signinwithpasskey')}
                    </button>
                </div>

                <!-- Inline password form (expanded).
                     Uses the same labeled, full-size inputs and primary
                     button styling as the bootstrap form below so the
                     branded login doesn't look like a stripped-down
                     variant of the main one. text-left overrides the
                     card-body's text-center so labels and the forgot-
                     password link line up properly. -->
                <div if="{state.subdomainShowPassword}" class="mt-3 w-full text-start">
                    <div if="{state.subdomainPrimaryProvider || state.subdomainSecondaryProviders.length > 0}" class="divider text-xs">{locale?.t('login_signinwithpassword')}</div>

                    <div class="form-control w-full">
                        <label class="label" for="login-form-subdomain-email">
                            <span class="label-text font-medium text-base text-base-content">{locale?.t('loginform_email')}</span>
                        </label>
                        <input
                            id="login-form-subdomain-email"
                            ref="subdomainEmailInput"
                            type="email"
                            name="username"
                            placeholder="{locale?.t('loginform_emailplaceholder')}"
                            autocomplete="username webauthn"
                            autofocus
                            class="input input-bordered w-full focus:input-primary transition-colors"
                            value="{state.email}"
                            oninput="{(e) => update({ email: e.target.value })}"
                            onkeydown="{(e) => e.key === 'Enter' && handlePasswordLogin()}"
                            disabled="{state.loading}"
                        />
                    </div>

                    <div class="form-control w-full mt-4">
                        <label class="label" for="login-form-subdomain-password">
                            <span class="label-text font-medium text-base text-base-content">{locale?.t('loginform_password')}</span>
                        </label>
                        <div class="relative">
                            <input
                                id="login-form-subdomain-password"
                                type="{state.showPassword ? 'text' : 'password'}"
                                name="password"
                                placeholder="{locale?.t('loginform_passwordplaceholder')}"
                                autocomplete="current-password"
                                class="input input-bordered w-full pe-12 focus:input-primary transition-colors"
                                value="{state.password}"
                                oninput="{(e) => update({ password: e.target.value })}"
                                onkeydown="{(e) => e.key === 'Enter' && handlePasswordLogin()}"
                                disabled="{state.loading}"
                            />
                            <button
                                type="button"
                                class="btn btn-ghost btn-sm absolute end-2 top-1/2 -translate-y-1/2 hover:bg-base-200"
                                onclick="{togglePasswordVisibility}"
                                tabindex="-1"
                                aria-label="{state.showPassword ? locale?.t('loginform_hidepassword') : locale?.t('loginform_showpassword')}"
                            >
                                <i class="bi {state.showPassword ? 'bi-eye-slash' : 'bi-eye'} text-base"></i>
                            </button>
                        </div>
                        <label class="label pt-1">
                            <span class="label-text-alt"></span>
                            <a href="#" class="label-text-alt link link-hover text-primary" onclick="{handleForgotPassword}">
                                {locale?.t('loginform_forgotpassword')}
                            </a>
                        </label>
                    </div>

                    <button class="btn btn-primary btn-lg w-full mt-2"
                        onclick="{handlePasswordLogin}"
                        disabled="{state.loading || !state.email || !state.password}">
                        <span if="{!state.loading}">
                            <i class="bi bi-box-arrow-in-right me-2 items-center"></i>
                            {locale?.t('loginform_signin')}
                        </span>
                        <span if="{state.loading}" class="loading loading-spinner loading-sm"></span>
                    </button>

                    <!-- Passkey: surfaced inline here only when no SSO is
                         configured (otherwise it lives in the collapsed
                         fallback block above). -->
                    <button if="{state.passkeySupported && !state.subdomainPrimaryProvider && state.subdomainSecondaryProviders.length === 0}" class="btn btn-outline w-full mt-3" onclick="{handlePasskeyLogin}">
                        <i class="bi bi-fingerprint me-2"></i>
                        {locale?.t('loginform_signinwithpasskey')}
                    </button>

                    <button if="{state.subdomainPrimaryProvider || state.subdomainSecondaryProviders.length > 0}" class="btn btn-ghost btn-sm w-full mt-2" onclick="{() => update({ subdomainShowPassword: false })}">
                        {locale?.t('login_backtosso')}
                    </button>
                </div>
            </div>

            <!-- Error -->
            <div if="{state.error}" class="alert alert-error mt-4 w-full">
                <i class="bi bi-exclamation-circle"></i>
                <span>{state.error}</span>
            </div>
        </div>
    </div>

    <!-- ================================================================ -->
    <!-- BOOTSTRAP / ADMIN LOGIN (existing form — hidden in subdomain mode) -->
    <!-- ================================================================ -->
    <div if="{!state.subdomainMode}" class="card bg-base-100 shadow-xl">
        <div class="card-body">
            <!-- Conditional Auth Status (for debugging) -->
            <!--  <div if="{state.conditionalAuthActive}" class="alert alert-info mb-4">
                <i class="bi bi-info-circle"></i>
                <span class="text-sm">
                    Passkey autofill is active. Click your passkey to login.
                </span>
            </div>  -->
            <!-- Show progress steps when OAuth login is in progress -->
            <auth-progress-steps 
                if="{state.oauthInProgress}"
                currentStep="{state.progressStep}"
                statusMessage="{state.progressMessage}"
                statusType="{state.progressType}"
            ></auth-progress-steps>

            <!-- Error Alert -->
            <div if="{state.error && !state.oauthInProgress}" class="alert alert-error mb-4">
                <i class="bi bi-exclamation-circle"></i>
                <span>{state.error}</span>
            </div>

            <!-- Success Alert -->
            <div if="{state.success && !state.oauthInProgress}" class="alert alert-success mb-4">
                <i class="bi bi-check-circle"></i>
                <span>{state.success}</span>
            </div>

            <!-- Email Input (hide during OAuth flow or passkey mode) -->
            <div if="{!state.oauthInProgress && !state.usePasskey}" class="form-control w-full">
                <label class="label" for="login-form-email">
                    <span class="label-text font-medium text-base text-base-content">{locale?.t('loginform_email')}</span>
                </label>
                <input
                    id="login-form-email"
                    ref="emailInput"
                    type="email"
                    name="username"
                    placeholder="{locale?.t('loginform_emailplaceholder')}"
                    autocomplete="username webauthn"
                    autofocus
                    class="input input-bordered w-full focus:input-primary transition-colors"
                    value="{state.email}"
                    oninput="{handleEmailInput}"
                    onkeypress="{handleKeyPress}"
                    disabled="{state.loading}"
                />
            </div>

            <!-- Password Input (hide during OAuth flow or if using passkey) -->
            <div if="{!state.usePasskey && !state.selectedOAuthProvider && !state.oauthInProgress}" class="form-control w-full mt-4">
                <label class="label" for="login-form-password">
                    <span class="label-text font-medium text-base text-base-content">{locale?.t('loginform_password')}</span>
                </label>
                <div class="relative">
                    <input
                        id="login-form-password"
                        type="{state.showPassword ? 'text' : 'password'}"
                        placeholder="{locale?.t('loginform_passwordplaceholder')}"
                        class="input input-bordered w-full pe-12 focus:input-primary transition-colors"
                        value="{state.password}"
                        oninput="{handlePasswordInput}"
                        onkeypress="{handleKeyPress}"
                        disabled="{state.loading}"
                    />
                    <button
                        type="button"
                        class="btn btn-ghost btn-sm absolute end-2 top-1/2 -translate-y-1/2 hover:bg-base-200"
                        onclick="{togglePasswordVisibility}"
                        tabindex="-1"
                        aria-label="{state.showPassword ? locale?.t('loginform_hidepassword') : locale?.t('loginform_showpassword')}"
                    >
                        <i class="bi {state.showPassword ? 'bi-eye-slash' : 'bi-eye'} text-base"></i>
                    </button>
                </div>
                <label class="label pt-1">
                    <span class="label-text-alt"></span>
                    <a href="#" class="label-text-alt link link-hover text-primary" onclick="{handleForgotPassword}">
                        {locale?.t('loginform_forgotpassword')}
                    </a>
                </label>
            </div>

            <!-- Login Buttons (hide during OAuth flow) -->
            <div if="{!state.oauthInProgress}" class="form-control mt-6 flex items-center">
                <!-- Password Login Button -->
                <button
                    if="{!state.usePasskey && !state.selectedOAuthProvider}"
                    class="btn btn-primary btn-lg w-full"
                    onclick="{handlePasswordLogin}"
                    disabled="{state.loading || !state.email || !state.password}"
                >
                    <span if="{!state.loading}">
                        <i class="bi bi-box-arrow-in-right me-2 items-center"></i>
                        {locale?.t('loginform_signin')}
                    </span>
                    <span if="{state.loading}" class="loading loading-spinner"></span>
                </button>

                <!-- Passkey Login Button -->
                <button
                    if="{state.usePasskey && state.passkeySupported && !state.selectedOAuthProvider}"
                    class="btn btn-primary btn-lg w-full"
                    onclick="{handlePasskeyLogin}"
                    disabled="{state.loading}"
                >
                    <span if="{!state.loading}">
                        <i class="bi bi-fingerprint me-2"></i>
                        {locale?.t('loginform_signinwithpasskey')}
                    </span>
                    <span if="{state.loading}" class="loading loading-spinner"></span>
                </button>

                <!-- OAuth Provider Button (when selected) -->
                <button
                    if="{state.selectedOAuthProvider}"
                    class="btn btn-primary btn-lg w-full max-w-xs"
                    onclick="{handleSelectedOAuthLogin}"
                    disabled="{state.loading}"
                >
                    <span if="{!state.loading}">
                        <i class="bi {getProviderIcon(state.selectedOAuthProvider.type)} me-2"></i>
                        {locale?.t('loginform_signinwith', { provider: state.selectedOAuthProvider.name })}
                    </span>
                    <span if="{state.loading}" class="loading loading-spinner"></span>
                </button>

            </div>

            <!-- Toggle between password and passkey -->
            <div if="{!state.oauthInProgress && state.passkeySupported && !state.selectedOAuthProvider}" class="text-center mt-3">
                <button
                    type="button"
                    class="btn btn-ghost btn-sm"
                    onclick="{toggleAuthMethod}"
                    disabled="{state.loading}"
                >
                    {state.usePasskey ? locale?.t('loginform_usepasswordinstead') : locale?.t('loginform_usepasskeyinstead')}
                </button>
            </div>

            <!-- Clear OAuth Selection -->
            <div if="{!state.oauthInProgress && state.selectedOAuthProvider}" class="text-center mt-3">
                <button
                    type="button"
                    class="btn btn-ghost btn-sm"
                    onclick="{clearOAuthSelection}"
                    disabled="{state.loading}"
                >
                    {locale?.t('loginform_choosedifferentmethod')}
                </button>
            </div>

            <!-- SYSTEM_ADMIN SSO entry (only shown with ?login=oauth).
                 Always drives the Cognito → AuthProxy → Google Workspace
                 flow via `environment.internal_sso`. This is intentionally
                 a single button rather than a DB-backed list — see
                 IdentityProviderController.getLoginOptions comments. -->
            <div if="{!state.oauthInProgress && state.showOAuthProviders}" class="divider">{locale?.t('loginform_or')}</div>

            <div if="{!state.oauthInProgress && !state.selectedOAuthProvider && state.showOAuthProviders}" class="space-y-2">
                <button
                    type="button"
                    class="btn btn-outline w-full"
                    onclick="{(e) => handleGoogleLogin(e)}"
                    disabled="{state.loading}"
                >
                    <i class="bi bi-google me-2"></i>
                    {locale?.t('loginform_continuewithgoogle')}
                </button>
            </div>

            <!-- Passkey Not Supported Warning. Gated on passkeySupportChecked
                 so the alert never renders during the brief window between
                 mount and the first feature-detection result — without the
                 flag, the initial state.passkeySupported = false would
                 flash this warning on every page load before onMounted
                 finishes setting it. -->
            <div if="{state.passkeySupportChecked && !state.passkeySupported && !state.oauthInProgress}"
                class="alert alert-warning mt-4">
                <i class="bi bi-exclamation-triangle"></i>
                <span class="text-sm">
                    {locale?.t('passkey_notsupported')}
                </span>
            </div>
        </div>
    </div>

    <script>
        import authProgressSteps from './auth-progress-steps.tag';
        import { getAuthService, getApi } from '../../services/service-loader';

        // Synchronous URL-only subdomain check. Used by onBeforeMount so
        // the bootstrap form doesn't flash on first paint when the URL
        // already tells us this is a branded-login mount — the bootstrap
        // form rendering first and then unmounting was stealing focus
        // (its email input has autofocus too) from the subdomain card's
        // email input that we want to land on. Same parsing rules as
        // detectSubdomainMode below; kept identical so the two can never
        // disagree about whether a candidate exists.
        function getSubdomainCandidateFromUrl() {
            let subdomain = null;
            const hash = window.location.hash || '';
            const [, qs] = hash.split('?');
            if (qs) {
                const params = new URLSearchParams(qs);
                subdomain = params.get('subdomain');
            }
            if (!subdomain) {
                const hostname = window.location.hostname.toLowerCase();
                const PLATFORM_SUFFIXES = ['.cloudfront.net', '.amazonaws.com', '.execute-api.', '.elb.amazonaws.com'];
                const isPlatformHost = PLATFORM_SUFFIXES.some(s => hostname.includes(s));
                if (!isPlatformHost) {
                    const parts = hostname.split('.');
                    if (parts.length >= 3) {
                        const candidate = parts[0];
                        const RESERVED_SUBDOMAINS = [
                            'www', 'app', 'api', 'localhost',
                            'dev', 'development', 'staging', 'stage', 'test', 'qa', 'preview', 'prod', 'production',
                        ];
                        if (!RESERVED_SUBDOMAINS.includes(candidate)) {
                            subdomain = candidate;
                        }
                    }
                }
            }
            return subdomain;
        }

        // Module-level in-flight dedupe for subdomain detection. The login
        // page can mount the form more than once during boot (the router's
        // mount-retry path on registration races + Riot's conditional
        // remount when state.quickLoginActive flips can each trigger a
        // second onMounted with the same subdomain) and without this
        // guard each mount fired its own /subdomains/resolve, /branding,
        // and /login-options round trip. Resolving once per subdomain
        // string and letting every mount await the same promise cuts
        // those duplicates without us having to chase down every code
        // path that can re-invoke detection.
        //
        // Keyed by subdomain so a navigation to a *different* subdomain
        // (theoretical: the SPA never changes hosts mid-session today,
        // but cheap to support) resolves fresh.
        const subdomainResolutionCache = new Map();

        async function resolveSubdomainBundle(subdomain) {
            if (subdomainResolutionCache.has(subdomain)) {
                return subdomainResolutionCache.get(subdomain);
            }
            const promise = (async () => {
                const api = await getApi();
                const resolveResp = await api.get(
                    `/subdomains/resolve?subdomain=${encodeURIComponent(subdomain)}`,
                    { skipLoading: true }
                );
                const mapping = resolveResp.data;
                if (!mapping || !mapping.customerId) {
                    return { mapping: null };
                }
                const [brandingResp, providersResp] = await Promise.allSettled([
                    api.get(`/customers/${mapping.customerId}/branding`, { skipLoading: true }),
                    api.get(`/multi-tenant/identity-providers/login-options?customerId=${mapping.customerId}`, { skipLoading: true }),
                ]);
                return { mapping, brandingResp, providersResp };
            })();
            subdomainResolutionCache.set(subdomain, promise);
            // Evict on failure so the next mount can retry rather than
            // be stuck repeating the same broken request indefinitely.
            promise.catch(() => subdomainResolutionCache.delete(subdomain));
            return promise;
        }

        export default {
            includeStore: true,

            get components() {

                return {
                authProgressSteps
            };

            },

            state: {
                email: '',
                password: '',
                showPassword: false,
                usePasskey: false,
                passkeySupported: false,
                // Gate for the "passkeys not supported" warning so it doesn't
                // flash before the feature-detection check completes — see
                // template note where the warning renders.
                passkeySupportChecked: false,
                conditionalAuthAvailable: false,
                conditionalAuthActive: false,
                loading: false,
                error: null,
                success: null,
                selectedOAuthProvider: null,
                oauthInProgress: false,
                progressStep: 1,
                progressMessage: '',
                progressType: 'info',
                showOAuthProviders: false,
                // Subdomain / customer login mode
                subdomainMode: false,
                subdomainLoading: false,
                subdomainCustomerId: null,
                subdomainPrimaryProvider: null,
                subdomainSecondaryProviders: [],
                subdomainShowPassword: false,
                // Mirrors Customer.ssoEnforcement from the branding endpoint.
                // 'REQUIRED' means the backend rejects password/passkey login
                // for non-breakglass users — the UI uses this to suppress the
                // auto-open-password behavior for the no-SSO case (otherwise
                // we'd invite users to type credentials the backend will 403).
                subdomainSsoEnforcement: 'OPTIONAL',
                customerBranding: null,
            },

            conditionalAbortController: null,
            conditionalAuthStarted: false,

            // Runs synchronously before the first paint. We use it to
            // short-circuit the bootstrap form when the URL already
            // tells us this is a branded-customer login mount — without
            // this, the bootstrap card rendered first, its autofocused
            // email input grabbed focus, and the subsequent swap to the
            // subdomain card lost it (visible as the password field not
            // being focused in Edge after auto-open). For non-subdomain
            // hosts (the regular login path) the helper returns null
            // synchronously and the bootstrap form renders on first
            // paint exactly as before — no perceptible delay.
            onBeforeMount() {
                const candidate = getSubdomainCandidateFromUrl();
                if (candidate) {
                    this.state.subdomainMode = true;
                    this.state.subdomainLoading = true;
                }
            },

            // Post-login navigation target. Reads ?returnto=<path> off the
            // current hash route (set by callers like accept-invitation.tag
            // when bouncing an unauthenticated user through login) and
            // validates it's a same-origin relative path. Falls back to the
            // caller's default (usually '/') when the param is missing or
            // looks wrong. Same validator the JIT EULA flow uses in
            // index.tag — only paths that start with a single '/' pass; '//'
            // (protocol-relative), 'https://', 'javascript:' etc. are
            // rejected so a malicious link can't redirect through us.
            getPostLoginDestination(fallback = '/') {
                try {
                    const hash = window.location.hash || '';
                    const qIdx = hash.indexOf('?');
                    if (qIdx === -1) return fallback;
                    const params = new URLSearchParams(hash.slice(qIdx + 1));
                    const raw = params.get('returnto');
                    if (!raw) return fallback;
                    if (!raw.startsWith('/') || raw.startsWith('//')) return fallback;
                    if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return fallback;
                    return raw;
                } catch (_) {
                    return fallback;
                }
            },

            async onMounted() {
                // Listen for the global EULA modal's broadcast acceptance event.
                // Every login path on this form opens the shared modal in
                // index.tag via document.querySelector('#eula-modal'), which
                // means our former local onAccept callback was never wired up
                // (duplicate IDs collapse to the first-rendered instance).
                // The modal always emits 'eula-accepted' on success, so the
                // window event is the reliable hook regardless of which login
                // method opened the modal — password, passkey, conditional
                // auth, OAuth-popup, etc.
                //
                // The _loginFormOpenedEula flag scopes our handler to flows
                // that THIS component initiated. Without it, the JWT-launch
                // EULA path on login.tag (which has its own returnto-aware
                // handler) would race with us and the last navigation wins.
                this._loginFormOpenedEula = false;
                this._eulaAcceptedHandler = (event) => {
                    if (!this._loginFormOpenedEula) return;
                    this._loginFormOpenedEula = false;
                    this.handleEulaAccepted(event?.detail || {});
                };
                window.addEventListener('eula-accepted', this._eulaAcceptedHandler);

                // Check if passkeys are supported (needed for both subdomain and bootstrap modes)
                const authService = await getAuthService();
                this.state.passkeySupported = authService.isPasskeySupported();
                this.state.passkeySupportChecked = true;
                this.update();

                // Detect subdomain login mode — if we're on a customer subdomain,
                // show their branded login instead of the bootstrap form.
                const subdomainDetected = await this.detectSubdomainMode();
                if (subdomainDetected) {
                    // Subdomain mode handles its own UI — skip bootstrap login setup
                    return;
                }

                // --- Bootstrap / admin login setup below ---
                this.conditionalAuthStarted = false;

                this.refreshOAuthParamState();

                // Re-read the ?login=oauth hint whenever the URL hash
                // changes — without this, navigating from #login to
                // #login?login=oauth on an already-mounted page requires
                // a hard refresh before the OAuth section appears.
                this._hashChangeHandler = () => this.refreshOAuthParamState();
                window.addEventListener('hashchange', this._hashChangeHandler);

                // Check conditional mediation availability AFTER mount (async check)
                // IMPORTANT: Only start once per component instance
                if (this.conditionalAuthStarted) {
                    console.log('[LoginForm] Conditional auth already started, skipping...');
                    return;
                }
                
                console.log('[LoginForm] Checking conditional mediation...');
                this.state.conditionalAuthAvailable = await authService.isConditionalMediationAvailable();
                console.log('[LoginForm] Conditional mediation available:', this.state.conditionalAuthAvailable);
                
                // Start conditional authentication if available
                if (this.state.conditionalAuthAvailable && !this.state.usePasskey) {
                    console.log('[LoginForm] Starting conditional auth...');
                    this.conditionalAuthStarted = true;
                    await this.startConditionalAuth();
                } else {
                    console.log('[LoginForm] Conditional auth not starting:', {
                        conditionalAuthAvailable: this.state.conditionalAuthAvailable,
                        usePasskey: this.state.usePasskey,
                        passkeySupported: this.state.passkeySupported
                    });
                }

                // Focus the email field so the user can start typing
                // immediately. Runs LAST in onMounted so it fires after the
                // conditional-auth navigator.credentials.get() call (which
                // opens the browser's autofill UI and was stealing focus
                // from a setTimeout-0 attempt placed earlier in mount).
                // requestAnimationFrame waits one paint past the current
                // tick — riot's refs and the input's DOM node are both
                // settled by then. Don't steal focus if the user has
                // already clicked into another input or the password
                // field, and skip when the email row is hidden (OAuth
                // flow / usePasskey mode).
                requestAnimationFrame(() => {
                    if (this.state.oauthInProgress || this.state.usePasskey) return;
                    const active = document.activeElement;
                    if (active && active !== document.body && active.tagName !== 'BUTTON') return;
                    this.refs?.emailInput?.focus();
                });
            },

            onBeforeUnmount() {
                // Clean up conditional auth
                this.stopConditionalAuth();
                if (this._hashChangeHandler) {
                    window.removeEventListener('hashchange', this._hashChangeHandler);
                    this._hashChangeHandler = null;
                }
                if (this._eulaAcceptedHandler) {
                    window.removeEventListener('eula-accepted', this._eulaAcceptedHandler);
                    this._eulaAcceptedHandler = null;
                }
            },

            // Read the `login=oauth` intent directly from the URL hash
            // (the router has already spliced any preserved query string
            // back onto the URL before route handling — see
            // handleRouteChange in router/routes.js). Runs on mount and
            // on every hashchange so adding ?login=oauth to an already-
            // mounted page takes effect without a hard refresh.
            refreshOAuthParamState() {
                const hash = window.location.hash || '';
                const hashParts = hash.split('?');
                const queryString = hashParts.length > 1 ? hashParts[1] : '';

                const next = queryString
                    ? new URLSearchParams(queryString).get('login') === 'oauth'
                    : false;

                if (this.state.showOAuthProviders !== next) {
                    this.update({ showOAuthProviders: next });
                }
            },


            async startConditionalAuth() {
                if (this.state.conditionalAuthActive) {
                    return; // Already active
                }

                console.log('[LoginForm] Starting conditional authentication...');
                const authService = await getAuthService();
                this.conditionalAbortController = await authService.startConditionalAuth(
                    (result) => this.handleConditionalAuthSuccess(result),
                    (error) => this.handleConditionalAuthError(error)
                );

                if (this.conditionalAbortController) {
                    this.state.conditionalAuthActive = true;
                    console.log('[LoginForm] Conditional authentication active - passkeys will appear in autofill');
                    
                    // Emit event to parent (login.tag) to start the 5-minute timeout
                    this.props.onConditionalAuthStarted?.();
                    
                    this.update();
                }
            },

            stopConditionalAuth() {
                if (this.conditionalAbortController) {
                    console.log('[LoginForm] Stopping conditional authentication');
                    this.conditionalAbortController.abort();
                    this.conditionalAbortController = null;
                    this.state.conditionalAuthActive = false;
                    
                    // Emit event to parent (login.tag) to clear the timeout
                    this.props.onConditionalAuthStopped?.();
                }
            },

            handleConditionalAuthSuccess(result) {
                console.log('[LoginForm] 🎉 Conditional auth successful!', result);
                this.state.loading = true;
                this.state.success = this.locale.t('loginform_loginsuccessful');
                this.state.conditionalAuthActive = false;
                
                // Stop conditional auth and clear parent timeout
                this.stopConditionalAuth();
                
                this.update();

                this.props.onLoginSuccess?.(result.user);

                // User just logged in with a passkey via conditional auth
                // Don't show passkey prompt - they already have one!
                // Just redirect immediately
                const dest = this.getPostLoginDestination();
                setTimeout(() => {
                    console.log('[LoginForm] User logged in with passkey, redirecting to', dest);
                    window.location.href = dest;
                }, 800);
            },

            async handleConditionalAuthError(error) {
                console.error('[LoginForm] ❌ Conditional auth error:', error);
                this.state.conditionalAuthActive = false;
                
                // Stop conditional auth and clear parent timeout
                this.stopConditionalAuth();
                
                // Check if error is EULA required
                // Error can be a string or an object with errorCode
                const isEulaError = (typeof error === 'object' && error.errorCode === 'eula_required') ||
                                   (typeof error === 'string' && error.includes('Subscription Agreement')) ||
                                   (error && error.message?.includes('Subscription Agreement'));
                
                if (isEulaError) {
                    console.log('[LoginForm] EULA acceptance required from conditional auth');
                    this.state.loading = false;
                    this.clearMessages();
                    this.update();

                    this.openEulaModalFromLoginForm('conditional auth');
                } else {
                    // Don't show other errors for conditional auth - it runs in background
                }
            },

            handleEmailInput(e) {
                this.state.email = e.target.value;
                this.clearMessages();
                this.update();
                
                // Log for debugging
                if (this.state.conditionalAuthActive) {
                    console.log('[LoginForm] Email input changed, conditional auth still active');
                }
            },

            handlePasswordInput(e) {
                this.state.password = e.target.value;
                this.clearMessages();
                this.update();
                
                // If user starts typing password, they want password login
                // Stop conditional auth to avoid conflicts
                if (this.state.password && this.state.conditionalAuthActive) {
                    console.log('[LoginForm] User entering password - stopping conditional auth');
                    this.stopConditionalAuth();
                }
            },

            handleKeyPress(e) {
                if (e.key === 'Enter') {
                    // If conditional auth is active AND no password entered, prevent submission
                    // The user should select from passkey autofill
                    if (this.state.conditionalAuthActive && !this.state.password) {
                        e.preventDefault();
                        console.log('[LoginForm] Conditional auth active, no password - use passkey autofill');
                        return;
                    }
                    
                    if (this.state.selectedOAuthProvider) {
                        this.handleSelectedOAuthLogin();
                    } else if (this.state.usePasskey) {
                        this.handlePasskeyLogin();
                    } else {
                        this.handlePasswordLogin();
                    }
                }
            },

            togglePasswordVisibility() {
                this.state.showPassword = !this.state.showPassword;
                this.update();
            },

            toggleAuthMethod() {
                this.state.usePasskey = !this.state.usePasskey;
                this.clearMessages();
                
                // Stop conditional auth when switching to passkey mode
                if (this.state.usePasskey) {
                    this.stopConditionalAuth();
                } else if (this.state.conditionalAuthAvailable) {
                    // Restart conditional auth when switching back to password mode
                    setTimeout(() => this.startConditionalAuth(), 100);
                }
                
                this.update();
            },

            clearOAuthSelection() {
                this.state.selectedOAuthProvider = null;
                this.update();
            },

            // Open the global EULA modal for a login-form-initiated flow.
            // Sets _loginFormOpenedEula so our window 'eula-accepted'
            // listener knows it owns the post-acceptance navigation, and
            // surfaces a friendly error if the dialog element somehow
            // isn't present (modal hasn't mounted yet, etc.).
            openEulaModalFromLoginForm(source) {
                const dialog = document.querySelector('#eula-modal');
                if (dialog && typeof dialog.showModal === 'function') {
                    console.log(`[LoginForm] Opening EULA dialog from ${source}`);
                    this._loginFormOpenedEula = true;
                    dialog.showModal();
                    return true;
                }
                console.error(`[LoginForm] EULA dialog not found (source: ${source})`);
                this.state.error = this.locale.t('loginform_unabletodisplayeula');
                this.update();
                return false;
            },

            async handlePasswordLogin() {
                console.log('[LoginForm] 🔐 handlePasswordLogin() called');
                console.log('[LoginForm] Email:', this.state.email);
                console.log('[LoginForm] Password length:', this.state.password?.length);
                
                this.state.loading = true;
                
                // Stop conditional auth immediately when user starts password login
                if (this.state.conditionalAuthActive) {
                    console.log('[LoginForm] Stopping conditional auth for password login');
                    this.stopConditionalAuth();
                }
                this.clearMessages();
                this.update();

                console.log('[LoginForm] Calling AuthService.loginWithPassword...');
                const authService = await getAuthService();
                const result = await authService.loginWithPassword(
                    this.state.email,
                    this.state.password
                );

                console.log('[LoginForm] Login result:', result);

                if (result.success) {
                    console.log('[LoginForm] ✅ Login successful!');

                    // Stop conditional auth immediately - user logged in another way
                    if (this.state.conditionalAuthActive) {
                        console.log('[LoginForm] Stopping conditional auth after successful password login');
                        this.stopConditionalAuth();
                    }

                    this.state.success = this.locale.t('loginform_loginsuccessful');
                    this.update();
                    
                    // DON'T call onLoginSuccess yet - it triggers immediate navigation via login.tag!
                    // this.props.onLoginSuccess?.(result.user);
                    
                    // Show passkey registration prompt after successful password login via parent
                    setTimeout(async () => {
                        console.log('[LoginForm] Attempting to show passkey prompt via parent...');
                        
                        let promptShown = false;
                        if (this.props.showPasskeyPrompt) {
                            promptShown = await this.props.showPasskeyPrompt();
                            console.log('[LoginForm] Prompt shown:', promptShown);
                        }
                        
                        // Only redirect if prompt not shown
                        if (!promptShown) {
                            console.log('[LoginForm] Prompt not shown, redirecting...');
                            this.props.onLoginSuccess?.(result.user);
                            window.location.href = this.getPostLoginDestination();
                        }
                    }, 300);
                } else {
                    // Check if error is EULA required
                    if (result.errorCode === 'eula_required') {
                        console.log('[LoginForm] EULA acceptance required from password login');
                        this.state.loading = false;
                        this.clearMessages();
                        this.update();

                        this.openEulaModalFromLoginForm('password login');
                    } else {
                        this.state.loading = false;
                        this.state.error = result.error;
                        this.update();
                    }
                }
            },

            /**
             * Handle EULA acceptance
             */
            async handleEulaAccepted(data) {
                console.log('[LoginForm] EULA accepted, account status:', data.accountStatus);

                // User accepted EULA, attempt to log in again (they should now be authenticated)
                // Simply refresh to trigger the authentication check
                this.state.loading = true;
                this.state.success = this.locale.t('loginform_eulaaccepted');
                this.update();
                
                const dest = this.getPostLoginDestination();
                setTimeout(() => {
                    // After accepting EULA, redirect to the post-login
                    // destination (returnto-aware) — user should be logged in.
                    window.location.href = dest;
                }, 500);
            },

            /**
             * Handle EULA cancellation
             * Note: Modal now handles logout directly, this is just for logging
             */
            handleEulaCancelled() {
                console.log('[LoginForm] EULA acceptance cancelled - user will be logged out');
            },

            async handlePasskeyLogin() {
                this.state.loading = true;
                this.clearMessages();
                this.update();

                // Use discoverable mode (no email required)
                const authService = await getAuthService();
                const result = await authService.loginWithPasskey(null);

                if (result.success) {
                    this.state.success = this.locale.t('loginform_loginsuccessful');
                    this.update();
                    
                    this.props.onLoginSuccess?.(result.user);

                    const dest = this.getPostLoginDestination();
                    setTimeout(() => {
                        window.location.href = dest;
                    }, 800);
                } else {
                    // Check if error is EULA required
                    if (result.errorCode === 'eula_required') {
                        console.log('[LoginForm] EULA acceptance required from passkey button');
                        this.state.loading = false;
                        this.clearMessages();
                        this.update();

                        this.openEulaModalFromLoginForm('passkey button');
                    } else {
                        this.state.loading = false;
                        this.state.error = result.error;
                        this.update();
                    }
                }
            },

            async handleSelectedOAuthLogin() {
                if (!this.state.selectedOAuthProvider) return;

                this.state.loading = true;
                this.state.oauthInProgress = true;
                this.state.progressStep = 1;
                this.state.progressMessage = this.locale.t('loginform_startingauth');
                this.state.progressType = 'info';
                this.clearMessages();
                this.update();

                // Step 2: OAuth Provider
                setTimeout(() => {
                    this.state.progressStep = 2;
                    this.state.progressMessage = this.locale.t('loginform_redirectingto', { provider: this.state.selectedOAuthProvider.name });
                    this.update();
                }, 500);

                const authService = await getAuthService();
                const result = await authService.loginWithOAuthPopup(this.state.selectedOAuthProvider);

                if (result.success) {
                    // Capture component reference
                    const self = this;

                    // Stop conditional auth immediately - user logged in via OAuth
                    if (self.state.conditionalAuthActive) {
                        console.log('[OAuth] Stopping conditional auth after successful OAuth login');
                        self.stopConditionalAuth();
                    }

                    // Step 3: Verify
                    self.state.progressStep = 3;
                    self.state.progressMessage = self.locale.t('loginform_verifyingauth');
                    self.update();

                    // Step 4: Success - show immediately without setTimeout
                    self.state.progressStep = 4;
                    self.state.progressMessage = self.locale.t('loginform_authsuccessful');
                    self.state.progressType = 'success';
                    self.update();

                    // Stop conditional auth before showing modal to avoid conflicts
                    if (self.state.conditionalAuthActive) {
                        console.log('[LoginForm] Stopping conditional auth before showing passkey prompt');
                        self.stopConditionalAuth();
                    }

                    // Use parent's centralized showPasskeyPrompt method.
                    // result.user came from /auth/me inside loginWithOAuthPopup,
                    // so it already has hasPasskey/eulaAccepted — pass it through
                    // to skip a duplicate /auth/me + /config/session roundtrip.
                    let promptShown = false;
                    if (self.props.showPasskeyPrompt) {
                        console.log('[LoginForm] Calling parent showPasskeyPrompt...');
                        promptShown = await self.props.showPasskeyPrompt(result.user);
                        console.log('[LoginForm] Prompt shown:', promptShown);
                    } else {
                        console.error('[LoginForm] showPasskeyPrompt not available in props');
                    }

                    // Only redirect if prompt not shown
                    if (!promptShown) {
                        console.log('[LoginForm] Prompt not shown, redirecting to home...');
                        self.props.onLoginSuccess?.(result.user);
                        window.location.href = self.getPostLoginDestination();
                    } else {
                        console.log('[LoginForm] Prompt is showing, waiting for user action...');
                    }
                } else {
                    this.state.loading = false;
                    this.state.oauthInProgress = false;
                    this.state.progressStep = 1;
                    this.state.error = result.error;
                    this.state.selectedOAuthProvider = null;
                    this.update();
                }
            },

            async handleGoogleLogin(e) {
                if (e) e.preventDefault();
                
                console.log('[GoogleLogin] handleGoogleLogin called, this:', this);
                console.log('[GoogleLogin] this.state:', this?.state);
                console.log('[GoogleLogin] this.refs:', this?.refs);
                
                this.state.loading = true;
                this.state.oauthInProgress = true;
                this.state.progressStep = 1;
                this.state.progressMessage = this.locale.t('loginform_startingauth');
                this.state.progressType = 'info';
                this.clearMessages();
                this.update();

                try {
                    console.log('[GoogleLogin] Starting async work...');

                    const authService = await getAuthService();
                    // Get environment config
                    let environment = this.store.getState().environment;
                    console.log('[GoogleLogin] Environment from Redux:', environment);

                    if (!environment || !environment.internal_sso) {
                        console.log('[GoogleLogin] Fetching environment from API...');
                        // Use api with baseURL override for root-level endpoint (not under /api/v1)
                        const api = await getApi();
                        const mode = import.meta.env.MODE;
                        const response = await api.get('/environment', {
                            baseURL: mode === 'development' ? 'http://localhost:8081' : '',
                            skipLoading: true
                        });
                        environment = response.data;
                        console.log('[GoogleLogin] Fetched environment:', environment);

                        this.store.dispatch({ type: 'setEnvironment', payload: environment });

                        if (!environment.internal_sso) {
                            throw new Error('OAuth URL not configured');
                        }
                    }

                    const googleProvider = {
                        name: 'Google',
                        type: 'OAUTH_GOOGLE',
                        loginUrl: environment.internal_sso
                    };

                    // Update to step 2
                    console.log('[GoogleLogin] Updating to step 2...');
                    this.state.progressStep = 2;
                    this.state.progressMessage = this.locale.t('loginform_redirectingtogoogle');
                    this.update();
                    
                    // Brief pause for UI update
                    await new Promise(r => setTimeout(r, 300));
                    
                    console.log('[GoogleLogin] Opening OAuth popup...');
                    const result = await authService.loginWithOAuthPopup(googleProvider);

                    console.log('[GoogleLogin] OAuth result:', result);

                    if (result.success) {
                        console.log('[GoogleLogin] OAuth successful, result:', result);
                        
                        // Capture component reference before async operations
                        const self = this;
                        
                        // Stop conditional auth immediately - user logged in via OAuth
                        if (self.state.conditionalAuthActive) {
                            console.log('[GoogleLogin] Stopping conditional auth after successful OAuth login');
                            self.stopConditionalAuth();
                        }
                        
                        self.state.progressStep = 3;
                        self.state.progressMessage = self.locale.t('loginform_verifyingauth');
                        self.update();

                        // Step 4: Success - show immediately without setTimeout
                        self.state.progressStep = 4;
                        self.state.progressMessage = self.locale.t('loginform_authsuccessful');
                        self.state.progressType = 'success';
                        self.update();

                        // Stop conditional auth before showing modal to avoid conflicts
                        if (self.state.conditionalAuthActive) {
                            console.log('[GoogleLogin] Stopping conditional auth before showing passkey prompt');
                            self.stopConditionalAuth();
                        }

                        // Use parent's centralized showPasskeyPrompt method.
                        // result.user came from /auth/me inside loginWithOAuthPopup,
                        // so it already has hasPasskey/eulaAccepted — pass it through
                        // to skip a duplicate /auth/me + /config/session roundtrip.
                        let promptShown = false;
                        if (self.props.showPasskeyPrompt) {
                            console.log('[GoogleLogin] Calling parent showPasskeyPrompt...');
                            promptShown = await self.props.showPasskeyPrompt(result.user);
                            console.log('[GoogleLogin] Prompt shown:', promptShown);
                        } else {
                            console.error('[GoogleLogin] showPasskeyPrompt not available in props');
                        }

                        // Only redirect if prompt not shown
                        if (!promptShown) {
                            console.log('[GoogleLogin] Prompt not shown, redirecting to home...');
                            self.props.onLoginSuccess?.(result.user);
                            window.location.href = self.getPostLoginDestination();
                        } else {
                            console.log('[GoogleLogin] Prompt is showing, waiting for user action...');
                        }
                    } else {
                        console.error('[GoogleLogin] OAuth failed');
                        console.error('[GoogleLogin] Full result object:', JSON.stringify(result, null, 2));
                        console.error('[GoogleLogin] Error message:', result.error);
                        
                        this.state.loading = false;
                        this.state.oauthInProgress = false;
                        this.state.progressStep = 1;
                        this.state.error = result.error;
                        this.update();
                    }
                } catch (error) {
                    console.error('[GoogleLogin] Error:', error);
                    this.state.loading = false;
                    this.state.oauthInProgress = false;
                    this.state.progressStep = 1;
                    this.state.error = error.message || 'Failed to start Google login';
                    this.update();
                }
            },

            handleForgotPassword(e) {
                e.preventDefault();
                window.location.hash = '#/forgot-password';
            },

            getProviderIcon(type) {
                switch (type) {
                    case 'OAUTH_GOOGLE': return 'bi-google';
                    case 'OAUTH_MICROSOFT': return 'bi-microsoft';
                    case 'SAML': return 'bi-shield-lock';
                    case 'COGNITO_NATIVE': return 'bi-key';
                    default: return 'bi-box-arrow-in-right';
                }
            },

            clearMessages() {
                this.state.error = null;
                this.state.success = null;
            },

            // ---- Subdomain / customer login mode ----

            /**
             * Expand the subdomain-mode password form (clicked from the
             * "Sign in with password" toggle in the collapsed fallback)
             * and put the cursor in the email field so the user can
             * start typing immediately. Same focus path used when the
             * form auto-opens because no SSO provider is configured.
             */
            showSubdomainPasswordForm() {
                this.update({ subdomainShowPassword: true });
                this.focusSubdomainEmail();
            },

            /**
             * Move focus to the subdomain-mode email input after the
             * next paint. Bails if the user has already focused another
             * input or a button so we don't steal an in-progress action.
             */
            focusSubdomainEmail() {
                requestAnimationFrame(() => {
                    const active = document.activeElement;
                    if (active && active !== document.body && active.tagName !== 'BUTTON') return;
                    this.refs?.subdomainEmailInput?.focus();
                });
            },

            /**
             * Detect whether the page is being accessed via a customer subdomain.
             * In production: extract from window.location.hostname (e.g. acme.app.metalark.ai → "acme")
             * In development: check for ?subdomain=acme query param for testing.
             * Returns true if subdomain mode was activated.
             */
            async detectSubdomainMode() {
                // Shared parsing — onBeforeMount uses the same helper so
                // initial-paint state already reflects whether we're on a
                // branded subdomain. By the time this runs, subdomainMode
                // and subdomainLoading are usually already set; we still
                // call update() at the bottom to cover the unlikely case
                // where someone constructs this component without
                // onBeforeMount having fired.
                const subdomain = getSubdomainCandidateFromUrl();
                if (!subdomain) return false;

                console.log('[LoginForm] Subdomain detected:', subdomain);
                this.state.subdomainMode = true;
                this.state.subdomainLoading = true;
                this.update();

                try {
                    // Resolve subdomain → customerId + primaryProviderId.
                    // Cached at module scope so a second mount of this
                    // component (router retry, conditional remount) reuses
                    // the same in-flight network round trip rather than
                    // firing /subdomains/resolve, /branding, and
                    // /login-options a second time.
                    const bundle = await resolveSubdomainBundle(subdomain);
                    const mapping = bundle.mapping;

                    if (!mapping || !mapping.customerId) {
                        console.warn('[LoginForm] Subdomain not found:', subdomain);
                        this.state.subdomainMode = false;
                        this.state.subdomainLoading = false;
                        this.update();
                        return false;
                    }

                    this.state.subdomainCustomerId = mapping.customerId;
                    console.log('[LoginForm] Subdomain resolved:', mapping);

                    const { brandingResp, providersResp } = bundle;

                    if (brandingResp.status === 'fulfilled') {
                        this.state.customerBranding = brandingResp.value.data;
                        // Stash enforcement so the provider-rendering block
                        // below can decide whether to auto-open password.
                        // Defaults to OPTIONAL when the field is missing —
                        // matches the Customer column default.
                        this.state.subdomainSsoEnforcement = brandingResp.value.data?.ssoEnforcement || 'OPTIONAL';

                        // Hoist the resolved branding to the page header in
                        // login.tag so the customer's logo and name appear
                        // above the returning-users list instead of below
                        // it. Optional prop — the form still works (just
                        // without the page-level branding) when mounted
                        // outside login.tag.
                        this.props.onBrandingResolved?.(brandingResp.value.data);

                        // Apply the customer's default language, but only if the user
                        // hasn't explicitly set their own preference in this browser.
                        const customerLang = brandingResp.value.data?.defaultLanguage;
                        const userHasPreference = localStorage.getItem('metalark_locale_login_language');
                        if (customerLang && !userHasPreference && this.locale && this.locale.getLanguageCode() !== customerLang) {
                            await this.locale.setLocale(customerLang, false);
                            this.update();
                        }
                    }

                    if (providersResp.status === 'fulfilled') {
                        const providers = providersResp.value.data?.identityProviders || [];
                        // Primary = the default provider, or the one matching the subdomain mapping
                        const primary = providers.find(p => p.isDefault) ||
                                        providers.find(p => p.id === mapping.primaryProviderId);
                        this.state.subdomainPrimaryProvider = primary || null;
                        this.state.subdomainSecondaryProviders = providers.filter(p => p !== primary);

                        // When the customer's branded login is configured
                        // with primaryProviderId = null and no other SSO
                        // providers exist, username/password is the
                        // intentional sign-in method — surface the inline
                        // form as the primary CTA rather than hiding it
                        // under a "Sign in with password" toggle.
                        //
                        // Skip this when ssoEnforcement === 'REQUIRED':
                        // the backend will 403 any password attempt, so
                        // auto-opening the form would invite a guaranteed
                        // failure. That case (SSO required AND no SSO
                        // provider) is genuinely misconfigured and falls
                        // through to the warning rendered below.
                        const noProviders = !primary && this.state.subdomainSecondaryProviders.length === 0;
                        const ssoRequired = this.state.subdomainSsoEnforcement === 'REQUIRED';
                        if (noProviders && !ssoRequired) {
                            this.state.subdomainShowPassword = true;
                        }
                    }

                    this.state.subdomainLoading = false;
                    this.update();

                    // When the password form is the primary CTA (no SSO
                    // providers, enforcement OPTIONAL) the user's keystrokes
                    // should land in the email field without them having to
                    // click first. requestAnimationFrame waits one paint so
                    // riot's ref is wired and the input is in the DOM.
                    if (this.state.subdomainShowPassword) {
                        this.focusSubdomainEmail();
                    }
                    return true;
                } catch (err) {
                    // A 404 from /subdomains/resolve just means this
                    // subdomain has no customer mapping — not an error
                    // worth flagging. Real failures (500, network) still
                    // surface via console.error so infra problems aren't
                    // silently swallowed.
                    if (err?.response?.status === 404) {
                        console.info('[LoginForm] No subdomain mapping, falling back to default login:', subdomain);
                    } else {
                        console.error('[LoginForm] Subdomain detection failed:', err);
                    }
                    this.state.subdomainMode = false;
                    this.state.subdomainLoading = false;
                    this.update();
                    return false;
                }
            },

            /**
             * Handle clicking a provider button in subdomain mode.
             * Reuses the existing OAuth popup flow.
             */
            async handleSubdomainProviderLogin(provider) {
                if (!provider.loginUrl) {
                    this.state.error = 'This provider is not configured for login';
                    this.update();
                    return;
                }

                // Reuse the existing OAuth popup infrastructure
                this.state.selectedOAuthProvider = provider;
                this.state.oauthInProgress = true;
                this.state.progressStep = 1;
                this.state.progressMessage = 'Starting authentication...';
                this.state.progressType = 'info';
                this.state.error = null;
                this.update();

                try {
                    const authService = await getAuthService();
                    const result = await authService.loginWithOAuthPopup(provider);

                    if (result.success) {
                        this.state.progressStep = 4;
                        this.state.progressMessage = 'Authentication successful!';
                        this.state.progressType = 'success';
                        this.update();

                        // Short delay then notify parent
                        setTimeout(() => {
                            this.props.onLoginSuccess?.(result.user);
                        }, 500);
                    } else {
                        this.state.oauthInProgress = false;
                        this.state.error = result.error || 'Authentication failed';
                        this.update();
                    }
                } catch (err) {
                    this.state.oauthInProgress = false;
                    this.state.error = err.message || 'Authentication failed';
                    this.update();
                }
            },
        }
    </script>
</login-form>